import { useEffect, useState } from "react";
import { DASH_URL, isRemote } from "@/lib/remote";

const CHECK_MS = 15000;

/**
 * Used from outside the restaurant (RestoYaar Connect, bible §64 rule 6): when the
 * restaurant stops answering, say so plainly instead of leaving numbers on screen
 * that look live. Renders nothing on the LAN.
 */
export default function RemoteBanner({ remote = isRemote(), check = CHECK_MS }) {
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!remote) return undefined;
    let alive = true;
    const probe = async () => {
      try {
        const r = await fetch("/api/", { cache: "no-store" });
        if (!alive) return;
        if (r.status === 503) {
          const body = await r.json().catch(() => ({}));
          setProblem(body.detail || "Remote access is switched off at the restaurant.");
        } else {
          setProblem(r.ok ? null : `The restaurant answered ${r.status}.`);
        }
      } catch {
        if (alive) setProblem("Lost connection to the restaurant. Its internet may be down.");
      }
    };
    probe();
    const id = setInterval(probe, check);
    const onOffline = () => setProblem("This device is offline.");
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", probe);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", probe);
    };
  }, [remote, check]);

  if (!remote || !problem) return null;
  return (
    <div role="alert" data-testid="remote-banner"
      className="fixed inset-x-0 top-0 z-[100] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-destructive px-4 py-2 text-center text-body font-medium text-destructive-foreground">
      <span>{problem} What you see may be out of date.</span>
      <a className="underline" href={DASH_URL}>Back to RestoYaar dash</a>
    </div>
  );
}
